import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { ArrowLeft, Save, X, Upload, Star } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { motion } from "framer-motion";
import PageHeader from "../admin/PageHeader";
import { fetchTestimonialById, editTestimonial } from "@/hooks/useTestimonials";
import ImageUploader from "../ui/ImageUploader";
import { testimonials } from "@/data/testimonials";

const EditTestimonial = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const statusOptions = ["active", "inActive"];
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    position: "",
    message: "",
    image: null,
    featured: false,
    company: "",
    projectTitle: "",
    country: "",
    rating: 5,
    status: "active",
  });

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const { testimonial } = await fetchTestimonialById(id);
        setFormData({
          name: testimonial.name,
          position: testimonial.position,
          company: testimonial.company || "",
          country: testimonial.country || "",
          message: testimonial.message,
          rating: testimonial.rating || 5,
          featured: testimonial.featured || false,
          status: testimonial.status || "active",
          projectTitle: testimonial.projectTitle || "",
          image: testimonial.images || null,
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch testimonial details.",
          variant: "destructive",
        });
        // navigate("/admin/testimonial");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id, navigate, toast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const payload = new FormData();
    payload.append("name", formData.name);
    payload.append("position", formData.position);
    payload.append("message", formData.message);
    payload.append("featured", formData.featured);
    payload.append("company", formData.company);
    payload.append("projectTitle", formData.projectTitle);
    payload.append("country", formData.country);
    payload.append("rating", formData.rating);
    payload.append("status", formData.status);

    if (formData.image) {
      payload.append("image", formData.image); // single file
    }

    const res = await editTestimonial(id, payload); // <-- use your update API hook

    if (res.success) {
      toast({
        title: "Testimonial Updated!",
        description: "The testimonial has been successfully updated.",
      });
      navigate("/admin/testimonials");
    }
  } catch (error) {
    toast({
      title: "Error",
      description:
        error.response?.data?.message ||
        "Failed to update testimonial. Please try again.",
      variant: "destructive",
    });
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <PageHeader
        title="Edit Testimonial"
        description="Update the testimonial details"
        routeTo="/admin/testimonials"
        backTo="Testimonials"
      />

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Testimonial Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter Client Name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="position">Position</Label>
                    <Input
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      placeholder="Enter Client Position (e.g., CEO)"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company">Company *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Enter Client Company Name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Enter Client Country Name"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Write the testimonial message..."
                    rows={4}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="projectTitle">Project Title *</Label>
                  <Input
                    id="projectTitle"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    placeholder="Enter Short Project Title"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rating">Rating (1-5)</Label>
                  <Input
                    type="number"
                    id="rating"
                    name="rating"
                    min="1"
                    max="5"
                    value={formData.rating}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="imageUrl">Profile Image URL</Label>
                  <div className="flex gap-2">
                    <ImageUploader
                      value={formData.image ? [formData.image] : []} // always array
                      onChange={
                        (files) =>
                          setFormData((prev) => ({ ...prev, image: files[0] })) // pick first file
                      }
                    />
                  </div>

                  {formData.image && (
                    <img
                      src={formData?.image?.url}
                      alt="Avatar Preview"
                      className="w-16 h-16 rounded-full mt-2 object-cover border"
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="featured">Featured Testimonial</Label>
                    <p className="text-sm text-muted-foreground">
                      Highlight this testimonial on the homepage
                    </p>
                  </div>
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, featured: checked }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                    />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? "Updating..." : "Update Testimonial"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/admin/testimonials")}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default EditTestimonial;
